import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, FormControl, InputLabel, Select,
  MenuItem, Grid, Divider, FormControlLabel, Switch, Card, CardContent,
  Chip, CircularProgress, Alert, InputAdornment, IconButton
} from '@mui/material';
import { 
  ArrowBack, Edit, Visibility, VisibilityOff, Save, Cancel 
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import useFetchObjects from '../../api/useFetchObjects';
import useUpdate from '../../api/useUpdate';
import { useAuth } from '../../context/AuthContext';
import { styled } from '@mui/system';
import { RTLTextField, RTLFormControl } from '../../styles/rtlComponents';
import useRTL from '../../hooks/useRTL';

// Using centralized RTL components from styles/rtlComponents.js

const UserEdit = () => {
  // Use 'users' as default namespace so we can reference keys directly
  const { t, i18n } = useTranslation(['users', 'common', 'validation']);
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const token = user?.token;
  const { isRTL, getRTLProps } = useRTL();

  // State
  const [showPassword, setShowPassword] = useState(false);
  const [passwordChange, setPasswordChange] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    username: '', email: '', position: '', role_id: '', ministry_id: '', department_id: '',
    directorate_id: '', deputy_ministry_id: '', is_provincial: false,
    province_id: '', is_active: true, password: '', full_name: ''
  });
  const [errors, setErrors] = useState({});
  const [filteredDeputyMinistries, setFilteredDeputyMinistries] = useState([]);
  const [filteredDirectorates, setFilteredDirectorates] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);

  // API Calls
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/users/${id}`;
  const { data: userData, isLoading, isError, error: fetchError } = useQuery({
    queryKey: ['users', id],
    queryFn: () => axios.get(apiUrl, { headers: { Authorization: `Token ${token}` } })
      .then(res => res.data)
  });

  const { data: rolesData } = useFetchObjects('roles', { limit: 1000, page: 1 }, token);
  const { data: ministriesData } = useFetchObjects('ministries', { limit: 1000, page: 1 }, token);
  const { data: departmentsData } = useFetchObjects('departments', { limit: 1000, page: 1 }, token);
  const { data: directoratesData } = useFetchObjects('directorates', { limit: 1000, page: 1 }, token);
  const { data: deputyMinistriesData } = useFetchObjects('deputy-ministries', { limit: 1000, page: 1 }, token);
  const { data: provincesData } = useFetchObjects('provinces', { limit: 1000, page: 1 }, token);
  
  // Normalize potential API payload shapes
  const unwrap = (d) => (Array.isArray(d) ? d : (d && Array.isArray(d.data) ? d.data : []));
  
  const roles = unwrap(rolesData);
  const ministries = unwrap(ministriesData);
  const departments = unwrap(departmentsData);
  const directorates = unwrap(directoratesData);
  const deputyMinistries = unwrap(deputyMinistriesData);
  const provinces = unwrap(provincesData);
  const { handleUpdate, loading: updateLoading, error: updateError } = useUpdate('users', token);

  // Effects
  useEffect(() => {
    if (userData) setFormData({
      username: userData.username || '',
      email: userData.email || '',
      full_name: userData.full_name || '',
      position: userData.position || '',
      role_id: userData.role_id || '',
      ministry_id: userData.ministry_id || '',
      department_id: userData.department_id || '',
      directorate_id: userData.directorate_id || '',
      deputy_ministry_id: userData.deputy_ministry_id || '',
      is_provincial: userData.is_provincial || false,
      province_id: userData.province_id || '',
      is_active: userData.is_active,
      password: '' // Don't populate password field for security
    });
  }, [userData]);

  // Filter effects like in UserCreate
  useEffect(() => {
    if (formData.ministry_id) {
      const filtered = deputyMinistries?.filter(dept => dept.ministry_id === formData.ministry_id) || [];
      setFilteredDeputyMinistries(filtered);
      if (formData.deputy_ministry_id && !filtered.some(dept => dept.id === formData.deputy_ministry_id)) {
        setFormData(prev => ({ ...prev, deputy_ministry_id: '', directorate_id: '', department_id: '' }));
      }
    } else {
      setFilteredDeputyMinistries([]);
      setFormData(prev => ({ ...prev, deputy_ministry_id: '', directorate_id: '', department_id: '' }));
    }
  }, [formData.ministry_id, deputyMinistries]);

  useEffect(() => {
    let filtered = [];
    if (formData.deputy_ministry_id) {
      filtered = directorates?.filter(dir => dir.deputy_ministry_id === formData.deputy_ministry_id) || [];
    } else if (formData.ministry_id) {
      filtered = directorates?.filter(dir => dir.ministry_id === formData.ministry_id && !dir.deputy_ministry_id) || [];
    }
    setFilteredDirectorates(filtered);
  }, [formData.ministry_id, formData.deputy_ministry_id, directorates]);

  useEffect(() => {
    let filtered = [];
    if (formData.directorate_id) {
      filtered = departments?.filter(dept => dept.directorate_id === formData.directorate_id) || [];
    } else if (formData.deputy_ministry_id) {
      filtered = departments?.filter(dept => dept.deputy_ministry_id === formData.deputy_ministry_id && !dept.directorate_id) || [];
    }
    setFilteredDepartments(filtered);
  }, [formData.directorate_id, formData.deputy_ministry_id, departments]);


  // Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev, 
      [name]: checked,
      ...(name === 'is_provincial' && !checked && { province_id: '' })
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = t('validation:required');
    if (passwordChange && formData.password && formData.password.length < 6) newErrors.password = t('validation:password_length');
    if (!formData.email.trim()) newErrors.email = t('validation:required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = t('validation:invalid_email');
    if (!formData.position.trim()) newErrors.position = t('validation:required');
    if (!formData.role_id) newErrors.role_id = t('validation:required');
    if (formData.is_provincial && !formData.province_id) newErrors.province_id = t('validation:required');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const updateData = { ...formData };
    // Only include password if it was changed and has a value
    if (!passwordChange || !updateData.password.trim()) {
      delete updateData.password;
    }

    try {
      await handleUpdate(id, {
        ...updateData,
        ministry_id: updateData.ministry_id || null,
        department_id: updateData.department_id || null,
        directorate_id: updateData.directorate_id || null,
        deputy_ministry_id: updateData.deputy_ministry_id || null,
        province_id: updateData.province_id || null
      });
      toast.success(t('update_success'));
      navigate('/users');
    } catch (err) {
      toast.error(err.response?.data?.message || t('error_updating', { ns: 'common' }));
    }
  };

  // Loading state
  const isLoadingData = !rolesData || !ministriesData || !deputyMinistriesData || !directoratesData || !departmentsData || !provincesData;

  if (isLoading || isLoadingData) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
      <CircularProgress />
    </Box>
  );

  if (isError) return (
    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
      {fetchError?.message || t('fetch_error')}
    </Alert>
  );

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'} sx={{ width: '100%' }}>
      <Box sx={{ 
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        mb: 3, flexDirection: isRTL ? 'row-reverse' : 'row', width: '100%'
      }}>
        <Button
          component={RouterLink}
          to="/users"
          startIcon={!isRTL && <ArrowBack />}
          endIcon={isRTL && <ArrowBack />}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {t('common:back')}
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <Typography variant="h4" component="h1" sx={{ textAlign: isRTL ? 'right' : 'left', fontWeight: 'bold' }}>
          {t('edit_title')}
        </Typography>
        <Chip 
          label={formData.is_active ? t('status_active') : t('status_inactive')}
          color={formData.is_active ? 'success' : 'error'} 
        />
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, textAlign: isRTL ? 'right' : 'left' }}>{error}</Alert>}

      <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            {/* Account Info Section */}
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, textAlign: isRTL ? 'right' : 'left', borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
              {t('account_info')}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <RTLTextField
                  size="small"
                  fullWidth required
                  name="username" label={t('username')}
                  value={formData.username} onChange={handleInputChange}
                  error={!!errors.username} helperText={errors.username}
                  {...getRTLProps('textfield')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RTLTextField
                  size="small"
                  fullWidth required
                  name="email" label={t('email')} type="email"
                  value={formData.email} onChange={handleInputChange}
                  error={!!errors.email} helperText={errors.email}
                  {...getRTLProps('textfield')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RTLTextField
                  size="small"
                  fullWidth
                  name="full_name" label={t('full_name')}
                  value={formData.full_name} onChange={handleInputChange}
                  error={!!errors.full_name} helperText={errors.full_name}
                  {...getRTLProps('textfield')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RTLTextField
                  size="small"
                  fullWidth
                  name="password" label={t('password')}
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password} 
                  onChange={(e) => { handleInputChange(e); setPasswordChange(true); }}
                  placeholder={t('password_placeholder')}
                  error={!!errors.password} 
                  helperText={errors.password || (passwordChange ? t('password_will_be_updated') : t('leave_blank_to_keep_current'))}
                  {...getRTLProps('textfield')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RTLTextField
                  size="small"
                  fullWidth required
                  name="position" label={t('position')}
                  value={formData.position} onChange={handleInputChange}
                  error={!!errors.position} helperText={errors.position}
                  {...getRTLProps('textfield')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RTLFormControl fullWidth required error={!!errors.role_id} {...getRTLProps('select')}>
                  <InputLabel>{t('role')}</InputLabel>
                  <Select
                    size="small"
                    name="role_id" label={t('role')}
                    value={formData.role_id} onChange={handleInputChange}
                  >
                    {roles.map(role => (
                      <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                    ))}
                  </Select>
                </RTLFormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={formData.is_active} onChange={handleSwitchChange} name="is_active" color="success" />}
                  label={t('active_status')}
                  sx={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}
                />
              </Grid>
            </Grid>
            
            {/* Organization Info Section */}
            <Typography variant="h6" sx={{ mt: 4, mb: 3, fontWeight: 600, textAlign: isRTL ? 'right' : 'left', borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
              {t('organization_info')}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <RTLFormControl fullWidth {...getRTLProps('select')}>
                  <InputLabel>{t('ministry')}</InputLabel>
                  <Select
                    size="small"
                    name="ministry_id" label={t('ministry')}
                    value={formData.ministry_id || ''} onChange={handleInputChange}
                  >
                    <MenuItem value=""><em>{t('none', { ns: 'common' })}</em></MenuItem>
                    {ministries.map(ministry => (
                      <MenuItem key={ministry.id} value={ministry.id}>{ministry.name}</MenuItem>
                    ))}
                  </Select>
                </RTLFormControl>
              </Grid>
              
              {formData.ministry_id && (
                <Grid item xs={12} md={6}>
                  <RTLFormControl fullWidth {...getRTLProps('select')}>
                    <InputLabel>{t('deputy_ministry')}</InputLabel>
                    <Select
                      size="small"
                      name="deputy_ministry_id" label={t('deputy_ministry')}
                      value={formData.deputy_ministry_id || ''} onChange={handleInputChange}
                    >
                      <MenuItem value=""><em>{t('none', { ns: 'common' })}</em></MenuItem>
                      {filteredDeputyMinistries.map(dm => (
                        <MenuItem key={dm.id} value={dm.id}>{dm.name}</MenuItem>
                      ))}
                    </Select>
                  </RTLFormControl>
                </Grid>
              )}
              
              {(formData.deputy_ministry_id || formData.ministry_id) && (
              <Grid item xs={12} md={6}>
                  <RTLFormControl fullWidth {...getRTLProps('select')}>
                  <InputLabel>{t('directorate')}</InputLabel>
                  <Select
                      size="small"
                    name="directorate_id" label={t('directorate')}
                    value={formData.directorate_id || ''} onChange={handleInputChange}
                  >
                    <MenuItem value=""><em>{t('none', { ns: 'common' })}</em></MenuItem>
                      {filteredDirectorates.map(dir => (
                        <MenuItem key={dir.id} value={dir.id}>{dir.name}</MenuItem>
                    ))}
                  </Select>
                  </RTLFormControl>
              </Grid>
              )}
              
              {(formData.directorate_id || formData.deputy_ministry_id) && (
              <Grid item xs={12} md={6}>
                  <RTLFormControl fullWidth {...getRTLProps('select')}>
                  <InputLabel>{t('department')}</InputLabel>
                  <Select
                      size="small"
                    name="department_id" label={t('department')}
                    value={formData.department_id || ''} onChange={handleInputChange}
                  >
                    <MenuItem value=""><em>{t('none', { ns: 'common' })}</em></MenuItem>
                      {filteredDepartments.map(dept => (
                        <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                    ))}
                  </Select>
                  </RTLFormControl>
              </Grid>
              )}
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch checked={formData.is_provincial} onChange={handleSwitchChange} name="is_provincial" color="primary" />}
                  label={t('is_provincial')}
                  sx={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}
                />
              </Grid>
              
              {formData.is_provincial && (
                <Grid item xs={12} md={6}>
                  <RTLFormControl fullWidth required error={!!errors.province_id} {...getRTLProps('select')}>
                    <InputLabel>{t('province')}</InputLabel>
                    <Select
                      size="small"
                      name="province_id" label={t('province')}
                      value={formData.province_id || ''} onChange={handleInputChange}
                    >
                      <MenuItem value=""><em>{t('none', { ns: 'common' })}</em></MenuItem>
                      {provinces.map(province => (
                        <MenuItem key={province.id} value={province.id}>{province.name}</MenuItem>
                      ))}
                    </Select>
                  </RTLFormControl>
                </Grid>
              )}
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                component={RouterLink}
                to="/users"
                startIcon={!isRTL && <Cancel />}
                endIcon={isRTL && <Cancel />}
              >
                {t('common:cancel')}
              </Button>
              <Button 
                variant="contained" 
                type="submit"
                disabled={updateLoading}
                startIcon={updateLoading ? <CircularProgress size={20} /> : <Save />}
              >
                {t('common:save')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserEdit;